import { RedirectToLogin, RedirectToSignup, RequiredAuthProvider } from '@propelauth/react';
import { Loader } from '@mantine/core';
import { useRouter } from 'next/router';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { env } from '~/env.mjs';
import { useAuthData } from '~/hooks/auth/use-auth-data';
import { setAccessToken } from '~/trpc-extra/api';

export const PUBLIC_ROUTES = ['/lite'];

// Otherwise header is too long for server and it fails
// Note: this is an approximation
const MAX_ACCESS_TOKEN_CHAR_LENGTH = 3500;

/**
 * Renders signup or login redirect based on `?signup=true` query param.
 * Marketing site (www.stably.ai) links with ?signup=true → new visitors see signup.
 * Returning users whose sessions expire see the default login page.
 */
function LoggedOutRedirect() {
  const router = useRouter();
  const wantsSignup = router.query.signup === 'true';
  return wantsSignup ? <RedirectToSignup /> : <RedirectToLogin />;
}

export default function StablyRequiredAuthProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const isPublicRoute = PUBLIC_ROUTES.some((route) => router.pathname.startsWith(route));

  return isPublicRoute ? children : (
      <RequiredAuthProvider
        authUrl={env.NEXT_PUBLIC_AUTH_URL}
        minSecondsBeforeRefresh={60}
        displayIfLoggedOut={<LoggedOutRedirect />}
      >
        <TokenSetter>{children}</TokenSetter>
      </RequiredAuthProvider>
    );
}

const TokenSetter = ({ children }: { children: ReactNode }) => {
  const {
    activeOrg,
    accessToken,
    tokens: { getAccessTokenForOrg },
  } = useAuthData();
  const activeOrgId = activeOrg?.orgId;

  const [isAccessTokenReady, setIsAccessTokenReady] = useState(false);

  // This will set the access token used by the trpc client
  // It initially sets the access token that may contain all user orgs but that can be too large
  // See: https://docs.propelauth.com/guides-and-examples/guides/active-org
  // So we instead try to get the one just for their active org.
  // Zero-org bootstrap intentionally relies on the unscoped token path when activeOrgId is absent.
  useEffect(() => {
    let isCurrent = true;

    void (async () => {
      const getScopedAccessToken = async () => {
        // Set the token if it's undefined or it fits in the header or they don't have an activeOrgId.
        // The zero-org bootstrap flow must still be able to reach authedProcedure endpoints.
        if (!accessToken || accessToken.length < MAX_ACCESS_TOKEN_CHAR_LENGTH || !activeOrgId) {
          setAccessToken(accessToken || undefined);
          setIsAccessTokenReady(!!accessToken);
        }
        // Otherwise we need to get a scoped token token using the activeOrgId
        if (activeOrgId) {
          const activeOrgAccessToken = await getAccessTokenForOrg(activeOrgId);
          if (isCurrent && activeOrgAccessToken.accessToken) {
            setAccessToken(activeOrgAccessToken.accessToken);
            setIsAccessTokenReady(true);
          }
        }
      };

      await getScopedAccessToken();
    })();

    return () => {
      isCurrent = false;
    };
  }, [accessToken, activeOrgId]);

  return isAccessTokenReady ? children : (
      <div className="flex h-screen items-center justify-center">
        <Loader />
      </div>
    );
};
